/**
 * DashboardDataView.jsx — read-only drill-down table for dashboards.
 *
 * Route: /dashboard/view/:viewKey?<params>
 *
 * Same card / table / pagination look as Enquiry.jsx, without the action bar
 * or status buttons. What it loads and which columns it shows come from the
 * matching entry in dashboardViews.jsx, so each dashboard click-through gets
 * columns suited to where the user clicked. Params live in the URL, so a
 * refresh or browser-back keeps the same view.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiChevronUp, FiChevronDown, FiGrid } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import AccessDenied from '../Components/AccessDenied';
import DASHBOARD_VIEWS from './dashboardViews';

const PAGE_LIMIT = 25;
const NO_COLUMNS = [];

const cellValue = (col, row) => (col.value ? col.value(row) : row[col.key]);

const compareValues = (a, b) => {
  if (a == null || a === '') return 1;
  if (b == null || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const DashboardDataView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewKey } = useParams();
  const [params] = useSearchParams();
  const { token, canAccessPage } = useAuth();
  const view = DASHBOARD_VIEWS[viewKey];

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sort, setSort] = useState({ key: '', dir: '' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);

  const paramsKey = params.toString();

  useEffect(() => {
    if (!view || !token) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setDenied(null);
      const result = await view.load({ params, token });
      if (cancelled) return;
      if (result?.denied) setDenied(result.denied);
      else if (result?.error) setError(result.error);
      else {
        setRows(result?.rows || []);
        setMeta(result?.meta ?? null);
      }
      setPage(1);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey, paramsKey, token]);

  const columns = view?.columns || NO_COLUMNS;

  const sortedRows = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key);
    if (!col || !sort.dir) return rows;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => sign * compareValues(cellValue(col, a), cellValue(col, b)));
  }, [rows, columns, sort]);

  const pages = Math.max(1, Math.ceil(sortedRows.length / limit));
  const pageRows = sortedRows.slice((page - 1) * limit, page * limit);
  const hasTotals = columns.some((c) => c.total);

  const toggleSort = (key) => {
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  };

  const goBack = (e) => {
    e.stopPropagation();
    if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  if (!view) return (
    <div className="vendor-page">
      <div className="management-card" style={{ padding: 40, textAlign: 'center', color: '#888' }}>
        Unknown dashboard view “{viewKey}”.
      </div>
    </div>
  );

  if (denied) return (
    <div className="vendor-page">
      <div className="management-card">
        <AccessDenied message={denied.message} required={denied.required} />
      </div>
    </div>
  );

  const subtitle = view.subtitle?.(params, meta);

  return (
    <div className="vendor-page">
      <div className="management-card">
        <div className="management-header" onClick={() => setIsCollapsed((v) => !v)}>
          <div className="header-title">
            {view.icon || <FiGrid size={18} />}
            <div>
              <h2>{view.title(params, meta)}</h2>
              {subtitle && <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{subtitle}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              type="button"
              onClick={goBack}
              title="Back"
              className="action-btn primary"
              style={{ padding: '0 12px', cursor: 'pointer' }}
            >
              <FiArrowLeft size={14} /> Back
            </button>
            {isCollapsed ? <FiChevronDown /> : <FiChevronUp />}
          </div>
        </div>

        {!isCollapsed && (
          <div className="vendor-table-container">
            {loading && rows.length === 0 ? (
              <div style={{ padding: 20 }}>Loading…</div>
            ) : error ? (
              <div style={{ padding: 20, color: '#e74c3c' }}>{error}</div>
            ) : (
              <>
                <table className="vendor-table">
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          className={col.mobileHide ? 'enquiry-table-mobile-hide' : undefined}
                          onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                          style={{
                            textAlign: col.align,
                            cursor: col.sortable ? 'pointer' : undefined,
                            userSelect: col.sortable ? 'none' : undefined,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {col.label}{' '}
                          {col.sortable && (sort.key === col.key
                            ? (sort.dir === 'asc' ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />)
                            : <span style={{ color: '#bbb' }}><FiChevronUp size={11} /></span>)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0
                      ? <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: 40 }}>{view.emptyText || 'No records found'}</td></tr>
                      : pageRows.map((row, i) => {
                        const link = view.rowLink?.(row, { canAccessPage });
                        return (
                          <tr
                            key={view.rowKey?.(row) ?? i}
                            onClick={link ? () => navigate(link, { state: { returnTo: `${location.pathname}${location.search}` } }) : undefined}
                            style={link ? { cursor: 'pointer' } : undefined}
                          >
                            {columns.map((col) => (
                              <td
                                key={col.key}
                                className={col.mobileHide ? 'enquiry-table-mobile-hide' : undefined}
                                style={{ textAlign: col.align }}
                              >
                                {col.render ? col.render(row) : (cellValue(col, row) ?? '—')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                  </tbody>
                  {hasTotals && sortedRows.length > 0 && (
                    <tfoot>
                      <tr style={{ fontWeight: 600, background: '#f8fafc' }}>
                        {columns.map((col, idx) => {
                          const totalRows = col.totalFilter ? sortedRows.filter(col.totalFilter) : sortedRows;
                          const sum = col.total ? totalRows.reduce((s, r) => s + (Number(cellValue(col, r)) || 0), 0) : null;
                          return (
                            <td
                              key={col.key}
                              className={col.mobileHide ? 'enquiry-table-mobile-hide' : undefined}
                              style={{ textAlign: col.align }}
                            >
                              {col.total ? (col.format ? col.format(sum) : sum) : idx === 0 ? 'Total' : ''}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>

                {/* Pagination */}
                {sortedRows.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '8px 16px', background: '#f8fafc', borderTop: '1px solid #eee' }}>
                    <select
                      value={limit}
                      onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12, cursor: 'pointer', height: 32 }}
                    >
                      {[25, 50, 75, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span style={{ fontSize: 12, color: '#888', marginRight: 'auto' }}>Page {page} of {pages}</span>
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                      style={{ display: 'inline-flex', alignItems: 'center', height: 32, padding: '0 14px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', fontSize: 12, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
                    >Prev</button>
                    <button
                      disabled={page >= pages}
                      onClick={() => setPage(page + 1)}
                      style={{ display: 'inline-flex', alignItems: 'center', height: 32, padding: '0 14px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', fontSize: 12, cursor: page >= pages ? 'not-allowed' : 'pointer', opacity: page >= pages ? 0.5 : 1 }}
                    >Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardDataView;
